import { useUpdateLeadStatusMutation } from '@/services/leads';
import { Box, Button, CheckboxInput, Modal, toast } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';

interface LeadManagementAcceptLeadModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  leadId: string;
  location: string;
}

export function LeadManagementAcceptLeadModal({
  isOpen,
  onDismiss,
  onConfirm,
  leadId,
  location,
}: LeadManagementAcceptLeadModalProps) {
  const [isUnderstood, setIsUnderstood] = useState(false);
  const [updateLeadStatus, { isLoading }] = useUpdateLeadStatusMutation();

  const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUnderstood(e.target.checked);
  }, []);

  const handleConfirm = useCallback(async () => {
    try {
      await updateLeadStatus({ leadId, status: 'ACCEPTED' }).unwrap();
      toast.success("Lead accepted. It's been added to your accounts.");
      onConfirm();
    } catch {
      toast.error('Failed to accept lead. Please try again.');
    }
  }, [leadId, onConfirm, updateLeadStatus]);

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} ariaLabelledBy="acceptLeadModal" maxWidth="4xl">
      <Modal.Header
        id="acceptLeadModal"
        title={
          (
            <>
              Are you sure you want to accept: <br />
              {location}?
            </>
          ) as unknown as string
        }
        onDismiss={onDismiss}
      />
      <Modal.Body background="secondary" childGap="md">
        <CheckboxInput
          id="accept-understanding"
          name="acceptUnderstanding"
          label="I understand that by accepting this lead I will contact the homeowner as soon as possible to progress the project forward."
          isChecked={isUnderstood}
          onChange={handleCheckboxChange}
        />
      </Modal.Body>
      <Modal.Footer>
        <Box direction="row" childGap="sm" justifyContent="space-between" width="100">
          <Button variant="secondary" tone="neutral" type="button" onClick={onDismiss}>
            Cancel
          </Button>
          <Button
            variant="primary"
            tone="primary"
            type="button"
            onClick={handleConfirm}
            isDisabled={!isUnderstood || isLoading}
          >
            Confirm acceptance
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  );
}
